import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useRouter } from 'next/router';

import { SCREENS } from '#/lib/utils';

/**
 * Header chrome theme driven by the section under the nav:
 * - `light`    → dark text; Cipher 5 logo
 * - `purple`   → light text; white logo
 * - `charcoal` → light text; Cipher 5 logo (never black)
 *
 * Default (no matching section): `light` → dark nav text.
 *
 * Only applied at the `ml` breakpoint and up — the mobile header is always
 * cloud / Cipher 5 and ignores theme + isScrolled.
 */
export type NavTheme = 'light' | 'purple' | 'charcoal';

export type NavDarkSurface = Exclude<NavTheme, 'light'>;

type RegisteredSection = {
  top: number;
  bottom: number;
  surface: NavTheme;
};

type NavThemeContextType = {
  theme: NavTheme;
  /** True once the page has scrolled past the transparent-header threshold. */
  isScrolled: boolean;
  registerSection: (
    id: string,
    top: number,
    bottom: number,
    surface?: NavTheme,
  ) => void;
  unregisterSection: (id: string) => void;
};

/** Desktop nav text when no section has registered a surface override. */
const DEFAULT_NAV_THEME: NavTheme = 'light';

/** Scroll distance (px) before the sticky header gains its frosted background. */
const HEADER_BG_SCROLL_THRESHOLD = 100;

/** Matches Tailwind `ml:` — desktop header chrome only. */
const DESKTOP_NAV_MQ = `(min-width: ${SCREENS.ml})`;

function initialNavThemeForPath(path: string): NavTheme {
  const pathname = path.split('?')[0];
  const normalized = pathname === '' ? '/' : pathname;
  if (normalized === '/') {
    return 'charcoal';
  }
  return DEFAULT_NAV_THEME;
}

type NavThemeProviderProps = {
  children: ReactNode;
};

const NavThemeContext = createContext<NavThemeContextType | undefined>(
  undefined,
);

export function useNavTheme(): NavThemeContextType {
  const context = useContext(NavThemeContext);

  if (context === undefined) {
    throw new Error('useNavTheme must be used within a NavThemeProvider');
  }

  return context;
}

export function NavThemeProvider({ children }: NavThemeProviderProps) {
  const { asPath } = useRouter();
  const [theme, setTheme] = useState<NavTheme>(() =>
    initialNavThemeForPath(asPath),
  );
  const [isScrolled, setIsScrolled] = useState(false);
  const sectionsRef = useRef(new Map<string, RegisteredSection>());
  const themeRef = useRef(theme);
  const isScrolledRef = useRef(isScrolled);
  const isDesktopRef = useRef(false);
  const rafRef = useRef<number | null>(null);

  const updateFromScroll = useCallback(() => {
    rafRef.current = null;

    // Mobile header doesn't use theme / frosted scroll styles.
    if (!isDesktopRef.current) {
      if (themeRef.current !== 'light') {
        themeRef.current = 'light';
        setTheme('light');
      }
      if (isScrolledRef.current) {
        isScrolledRef.current = false;
        setIsScrolled(false);
      }
      return;
    }

    const y = Math.max(0, window.scrollY);

    let match: RegisteredSection | undefined;
    for (const section of sectionsRef.current.values()) {
      if (y >= section.top && y < section.bottom) {
        match = section;
        break;
      }
    }

    const newTheme: NavTheme = match ? match.surface : DEFAULT_NAV_THEME;
    const newIsScrolled = y >= HEADER_BG_SCROLL_THRESHOLD;

    if (newTheme !== themeRef.current) {
      themeRef.current = newTheme;
      setTheme(newTheme);
    }
    if (newIsScrolled !== isScrolledRef.current) {
      isScrolledRef.current = newIsScrolled;
      setIsScrolled(newIsScrolled);
    }
  }, []);

  const scheduleUpdate = useCallback(() => {
    if (rafRef.current != null) return;
    rafRef.current = window.requestAnimationFrame(updateFromScroll);
  }, [updateFromScroll]);

  const registerSection = useCallback(
    (
      sectionId: string,
      top: number,
      bottom: number,
      surface: NavTheme = DEFAULT_NAV_THEME,
    ) => {
      sectionsRef.current.set(sectionId, { top, bottom, surface });
      if (isDesktopRef.current) {
        scheduleUpdate();
      }
    },
    [scheduleUpdate],
  );

  const unregisterSection = useCallback(
    (sectionId: string) => {
      sectionsRef.current.delete(sectionId);
      if (isDesktopRef.current) {
        scheduleUpdate();
      }
    },
    [scheduleUpdate],
  );

  useEffect(() => {
    const mq = window.matchMedia(DESKTOP_NAV_MQ);
    let listeningDesktop = false;

    const syncViewport = () => {
      const wasDesktop = isDesktopRef.current;
      isDesktopRef.current = mq.matches;

      if (mq.matches && !listeningDesktop) {
        window.addEventListener('scroll', scheduleUpdate, { passive: true });
        // Section rects shift on resize at desktop widths.
        window.addEventListener('resize', scheduleUpdate);
        listeningDesktop = true;
      } else if (!mq.matches && listeningDesktop) {
        window.removeEventListener('scroll', scheduleUpdate);
        window.removeEventListener('resize', scheduleUpdate);
        listeningDesktop = false;
      }

      // Recompute when entering desktop, or once when leaving so state resets.
      if (mq.matches || wasDesktop) {
        scheduleUpdate();
      }
    };

    syncViewport();
    mq.addEventListener('change', syncViewport);

    return () => {
      mq.removeEventListener('change', syncViewport);
      if (listeningDesktop) {
        window.removeEventListener('scroll', scheduleUpdate);
        window.removeEventListener('resize', scheduleUpdate);
      }
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [scheduleUpdate]);

  // Sections remount / scroll resets across navigations.
  useEffect(() => {
    if (isDesktopRef.current) {
      scheduleUpdate();
    }
  }, [asPath, scheduleUpdate]);

  return (
    <NavThemeContext.Provider
      value={{
        // Data Rights Unwrapped heroes sit on cipher purple.
        theme: asPath.includes('data-rights-unwrapped') ? 'purple' : theme,
        isScrolled,
        registerSection,
        unregisterSection,
      }}
    >
      {children}
    </NavThemeContext.Provider>
  );
}
